import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  User, Package, MapPin, LogOut, Loader2, ShieldCheck, Truck,
  ExternalLink, ChevronRight, ShoppingBag, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/common/Seo";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";
import { OtpAuth } from "@/components/auth/OtpAuth";

const TABS = [
  { id: "orders", label: "Orders", icon: Package },
  { id: "addresses", label: "Addresses", icon: MapPin },
  { id: "profile", label: "Profile", icon: User },
];

function StatusBadge({ status }) {
  if (!status) return null;
  const s = String(status).toLowerCase();
  const tone = s.includes("paid") || s.includes("fulfilled") || s.includes("success")
    ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/20"
    : s.includes("pending") || s.includes("transit") || s.includes("progress")
    ? "text-amber-300 bg-amber-500/10 border-amber-500/20"
    : "text-white/70 bg-white/5 border-white/10";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize ${tone}`}>
      {s.replace(/_/g, " ")}
    </span>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.accountOrders().then((d) => setOrders(d.orders || [])).catch(() => setOrders([]));
  }, []);

  if (orders === null) {
    return <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-[16px] bg-white/5" />)}</div>;
  }
  if (orders.length === 0) {
    return (
      <div className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-10 text-center">
        <Package size={36} className="mx-auto text-white/20" />
        <p className="mt-4 font-display text-lg font-bold">No orders yet</p>
        <p className="mt-1 text-[14px] text-white/50">When you place an order it will appear here with live tracking.</p>
        <Link to="/shop" className="mt-5 inline-flex items-center gap-2 rounded-[12px] bg-white px-5 py-2.5 text-[14px] font-semibold text-black transition-colors hover:bg-white/90">
          Start shopping <ArrowRight size={15} />
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="account-orders">
      {orders.map((o) => (
        <div key={o.id} data-testid="account-order" className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--acx-border)] pb-3">
            <div className="flex items-center gap-3">
              <span className="font-display text-[15px] font-bold">{o.name}</span>
              <StatusBadge status={o.financialStatus} />
              {o.fulfillmentStatus && <StatusBadge status={o.fulfillmentStatus} />}
            </div>
            <span className="text-[13px] text-white/45">
              {o.processedAt ? new Date(o.processedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {o.items?.slice(0, 5).map((it, idx) => (
              <div key={idx} className="flex items-center gap-2">
                {it.image ? <img src={it.image} alt="" className="h-12 w-12 rounded-[8px] border border-[color:var(--acx-border)] object-cover" /> : null}
                <span className="text-[13px] text-white/60">{it.title}<span className="text-white/35"> ×{it.quantity}</span></span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[14px] font-semibold">{formatINR(Number(o.total || 0))}</span>
            <div className="flex items-center gap-2">
              {(o.tracking || []).filter((t) => t?.url).map((t, i) => (
                <a key={t.url || i} href={t.url} target="_blank" rel="noreferrer"
                   data-testid="order-tracking-link"
                   className="inline-flex items-center gap-1.5 rounded-[10px] border border-[#E11B22]/40 bg-[#E11B22]/10 px-3 py-1.5 text-[12px] font-semibold text-[#E11B22] transition-colors hover:bg-[#E11B22]/20">
                  <Truck size={13} /> Track{t.company ? ` · ${t.company}` : ""} <ExternalLink size={11} />
                </a>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AddressesTab() {
  const [addresses, setAddresses] = useState(null);
  useEffect(() => {
    api.accountAddresses().then((d) => setAddresses(d.addresses || [])).catch(() => setAddresses([]));
  }, []);
  if (addresses === null) {
    return <div className="grid gap-3 sm:grid-cols-2">{[1, 2].map((i) => <div key={i} className="h-36 animate-pulse rounded-[16px] bg-white/5" />)}</div>;
  }
  if (addresses.length === 0) {
    return (
      <div className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-10 text-center">
        <MapPin size={36} className="mx-auto text-white/20" />
        <p className="mt-4 font-display text-lg font-bold">No saved addresses</p>
        <p className="mt-1 text-[14px] text-white/50">Addresses you use at checkout will be saved to your Shopify account.</p>
      </div>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="account-addresses">
      {addresses.map((a) => (
        <div key={a.id} className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-5">
          <div className="flex items-center justify-between">
            <span className="text-[14px] font-semibold">{[a.firstName, a.lastName].filter(Boolean).join(" ") || "Address"}</span>
            {a.isDefault && <span className="rounded-full border border-[#E11B22]/30 bg-[#E11B22]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#E11B22]">Default</span>}
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-white/60">
            {a.address1}{a.address2 ? `, ${a.address2}` : ""}<br />
            {[a.city, a.province, a.zip].filter(Boolean).join(", ")}<br />
            {a.country}{a.phone ? ` · ${a.phone}` : ""}
          </p>
        </div>
      ))}
    </div>
  );
}

function ProfileTab({ customer }) {
  return (
    <div className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-6" data-testid="account-profile">
      <dl className="grid gap-4 sm:grid-cols-2">
        <div>
          <dt className="text-[11px] uppercase tracking-[0.16em] text-white/40">Name</dt>
          <dd className="mt-1 text-[15px] font-medium text-white">{[customer.firstName, customer.lastName].filter(Boolean).join(" ") || "—"}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-[0.16em] text-white/40">Email</dt>
          <dd className="mt-1 text-[15px] font-medium text-white">{customer.email || "—"}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-[0.16em] text-white/40">Phone</dt>
          <dd className="mt-1 text-[15px] font-medium text-white">{customer.phone || "—"}</dd>
        </div>
      </dl>
    </div>
  );
}

export default function Account() {
  const [state, setState] = useState({ loading: true, authenticated: false, customer: null });
  const [tab, setTab] = useState("orders");

  const load = useCallback(() => {
    api.accountMe()
      .then((d) => setState({ loading: false, authenticated: d.authenticated, customer: d.customer }))
      .catch(() => setState({ loading: false, authenticated: false, customer: null }));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => {
    try {
      const { logoutUrl } = await api.accountLogout();
      if (logoutUrl) { window.location.href = logoutUrl; return; }
    } catch (e) {
      console.error("Logout request failed; clearing session locally", e);
    }
    toast.success("Signed out");
    setState({ loading: false, authenticated: false, customer: null });
  };

  if (state.loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-[var(--acx-bg)]">
        <Loader2 size={26} className="animate-spin text-[#E11B22]" />
      </div>
    );
  }

  if (!state.authenticated) {
    return (
      <div className="bg-[var(--acx-bg)]">
        <Seo title="My Account" description="Sign in to view your AutoCareX orders, tracking and addresses." noindex />
        <div className="acx-container flex min-h-[68vh] flex-col items-center justify-center px-4 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E11B22]/10">
            <User size={28} className="text-[#E11B22]" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold tracking-[-0.02em]">My Account</h1>
          <OtpAuth onAuthed={load} />
          <p className="mt-5 flex items-center gap-1.5 text-[12px] text-white/40">
            <ShieldCheck size={14} /> Secured with OTP verification
          </p>
        </div>
      </div>
    );
  }

  const c = state.customer || {};
  const greeting = c.firstName ? `Welcome back, ${c.firstName}` : "My Account";

  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo title="My Account" description="Your AutoCareX orders, tracking and addresses." noindex />
      <div className="acx-container px-4 py-10 sm:px-6 lg:px-10 lg:py-14">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-[-0.02em] sm:text-4xl" data-testid="account-greeting">{greeting}</h1>
            {c.email && <p className="mt-1.5 text-[14px] text-white/50">{c.email}</p>}
          </div>
          <button onClick={handleLogout} data-testid="account-logout-button"
                  className="inline-flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] px-4 py-2.5 text-[13px] font-semibold text-white/80 transition-colors hover:bg-white/5 hover:text-white">
            <LogOut size={15} /> Sign out
          </button>
        </motion.div>

        <div className="mt-8 flex gap-2 border-b border-[color:var(--acx-border)]">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`account-tab-${t.id}`}
                className={`relative inline-flex items-center gap-2 px-4 py-3 text-[14px] font-semibold transition-colors ${active ? "text-white" : "text-white/45 hover:text-white/70"}`}>
                <Icon size={16} /> {t.label}
                {active && <motion.span layoutId="acct-tab" className="absolute inset-x-0 -bottom-px h-0.5 bg-[#E11B22]" />}
              </button>
            );
          })}
        </div>

        <div className="mt-7">
          {tab === "orders" && <OrdersTab />}
          {tab === "addresses" && <AddressesTab />}
          {tab === "profile" && <ProfileTab customer={c} />}
        </div>
      </div>
    </div>
  );
}
